import { StatusCodes } from 'http-status-codes'

import { getAxios } from '../../api/axios'

import type { Brand, AssetType } from '@xcel-next/core/enums'
import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import type { CustomerBillingAddress, ProfileAsset, AssetSchemaType, PublicPaymentMethod, PurSubscriptionProduct, UserPurSubscription } from '@xcel-next/types'

type OrcConfig = AxiosRequestConfig & { baseURL: string }

const is4xxError = (error: any) => error.response?.status >= StatusCodes.BAD_REQUEST && error.response?.status < StatusCodes.INTERNAL_SERVER_ERROR
const ignore4xxList = ['/user/login', '/user/register', '/user/captcha', '/subscriptions/pur']
const isIgnoredPath = (url: string) => ignore4xxList.some(path => url.endsWith(path))

let axiosInstancePromise: Promise<AxiosInstance> | undefined

const getAxiosInstance = (): Promise<AxiosInstance> => {
  axiosInstancePromise ??= getAxios().then(axios => {
    const axiosInstance = axios.create()

    axiosInstance.interceptors.request.use(requestConfig => {
      requestConfig.headers = requestConfig.headers || {}
      return requestConfig
    })

    axiosInstance.interceptors.response.use(
      response => response,
      error => {
        // Skip handling for 4xx errors on register + login because already handled in the component
        if (is4xxError(error) && isIgnoredPath(error.request.responseURL)) {
          return Promise.reject(error)
        }
        const responseErrorMessage = error.response?.data?.message
        // Set details for error reporting to backend /log
        if (responseErrorMessage) {
          error.message += ' : ' + responseErrorMessage
        }

        let err: unknown = String(error)
        if (axios.isAxiosError(error)) {
          err = error.toJSON()
        }
        console.warn('Error from backend', error.request?.responseURL, err)

        return Promise.reject(error)
      }
    )

    return axiosInstance
  }).catch(error => {
    axiosInstancePromise = undefined
    throw error
  })

  return axiosInstancePromise
}

// kept for backwards compatibility — store reference not currently used
export function setStore (_store: unknown) {}

/* User APIs */

// mirrors services/orchestration-api/src/util/captcha.ts's CaptchaAnswer - signature/expiryTimestamp are required to verify the input server-side
export type CaptchaAnswer = {
  signature: string,
  expiryTimestamp: number,
  input: string
}

export async function register (config: OrcConfig, email: string, password: string, acceptedTermsAndConditions: boolean, acceptedDataPrivacyPolicy: boolean, brand: Brand, role: string, newsletter?: boolean, captcha?: CaptchaAnswer): Promise<void> {
  await (await getAxiosInstance()).post('/user/register', {
    email,
    password,
    acceptedTermsAndConditions,
    acceptedDataPrivacyPolicy,
    brand,
    role,
    newsletter,
    captcha
  }, config)
}

export async function getCaptcha (config: OrcConfig): Promise<unknown> {
  const { data } = await (await getAxiosInstance()).get('/user/captcha', config)
  return data
}


export async function activate (config: OrcConfig, oneTimePassword: string, newsletter: boolean | undefined, brand: Brand): Promise<{ token?: string }> {
  const { data } = await (await getAxiosInstance()).post('/user/activate', {
    oneTimePassword,
    newsletter,
    brand
  }, config)
  return data
}

export async function login (config: OrcConfig, email: string, password: string, brand: Brand): Promise<string> {
  const { data } = await (await getAxiosInstance()).post('/user/login', {
    email,
    password,
    brand
  }, config)
  return data
}

export async function facebookLogin (config: OrcConfig, accessToken: string, facebookId: string, brand: Brand): Promise<string> {
  const { data } = await (await getAxiosInstance()).post('/user/login/facebook', {
    accessToken,
    facebookId,
    brand
  }, config)
  return data
}

export async function facebookRegister (config: OrcConfig, accessToken: string, facebookId: string, brand: Brand): Promise<string> {
  const { data } = await (await getAxiosInstance()).post('/user/register/facebook', {
    accessToken,
    facebookId,
    brand
  }, config)
  return data
}

export async function getOneTimePassword (config: OrcConfig, _userId: string | undefined, brand: Brand): Promise<string> {
  const { data } = await (await getAxiosInstance()).post('/user/get-otp', {
    brand
  }, config)
  return data.oneTimePassword
}

export async function resetPassword (config: OrcConfig, email: string, brand: Brand): Promise<unknown> {
  const { data } = await (await getAxiosInstance()).post('/user/reset-password', { email, brand }, config)
  return data
}

export async function getUserProfile (config: OrcConfig): Promise<ProfileAsset> {
  const { data } = await (await getAxiosInstance()).get('/user/profile', config)
  return data
}

/* Assets */
export async function getAssetByQuery<T extends AssetType> (config: OrcConfig, params: { type: T, [key: string]: unknown }): Promise<AssetSchemaType<T> | undefined> {
  const { data } = await (await getAxiosInstance()).get(`/assets/${params.type.toLowerCase()}s`, {
    ...config,
    params
  } as AxiosRequestConfig)
  if (data.total !== 0) {
    return data.results[0] as AssetSchemaType<T>
  }
  return undefined
}

export async function checkDisplayNameExists (config: OrcConfig, params: unknown): Promise<boolean> {
  const { data } = await (await getAxiosInstance()).get('/assets/profiles/exists', {
    ...config,
    params
  })
  return data.exists
}

export async function postAsset<T extends AssetType> (config: OrcConfig, params: { type: T, asset: AssetSchemaType<T> }): Promise<AssetSchemaType<T> | undefined> {
  const { data } = await (await getAxiosInstance()).post<AssetSchemaType<T>>(`/assets/${params.type.toLowerCase()}s?returnEntity=true`, params.asset, config)
  return data
}

export async function postPublicAsset<T extends AssetType> (config: OrcConfig, params: { type: T, asset: AssetSchemaType<T> }, brand: Brand): Promise<AssetSchemaType<T> | undefined> {
  const { data } = await (await getAxiosInstance()).post<AssetSchemaType<T>>(`/assets/${params.type.toLowerCase()}s/public/${brand}`, params.asset, config)
  return data
}

export async function patchAsset<T extends AssetType> (config: OrcConfig, params: { type: T, id: string, asset: AssetSchemaType<T> }): Promise<AssetSchemaType<T> | undefined> {
  const { data } = await (await getAxiosInstance()).patch<AssetSchemaType<T>>(`/assets/${params.type.toLowerCase()}s/${params.id}`, params.asset, config)
  return data
}

export async function changePassword (config: OrcConfig, oldPassword: string, newPassword: string): Promise<void> {
  await (await getAxiosInstance()).post('/user/change-password', { oldPassword, newPassword }, config)
}

export async function changePasswordOtp (config: OrcConfig, oneTimePassword: string, newPassword: string, brand: Brand): Promise<void> {
  await (await getAxiosInstance()).post('/user/change-password-otp', { oneTimePassword, newPassword, brand }, config)
}

export async function deleteUser (config: OrcConfig): Promise<unknown> {
  const { data } = await (await getAxiosInstance()).delete('/user', config)
  return data
}

export async function newsletterSubscribe (config: OrcConfig, brand: Brand, email: string, type: string): Promise<void> {
  await (await getAxiosInstance()).post('/newsletter/subscribe/', {
    brand, email, type
  }, config)
}

export async function refreshAuthToken (config: OrcConfig): Promise<string> {
  const { data } = await (await getAxiosInstance()).post('/user/renew-token', {}, config)
  return data.token
}

export async function loadFeatureFlags (config: OrcConfig): Promise<unknown[]> {
  const { data } = await (await getAxiosInstance()).get('/flags', config)
  return data.flags
}

export async function getPaymentMethods (config: OrcConfig): Promise<PublicPaymentMethod[]> {
  const { data } = await (await getAxiosInstance()).get<PublicPaymentMethod[]>('/pur-subscription/customers/payment-methods', config)
  return data
}

export async function deletePaymentMethod (config: OrcConfig, paymentMethodId: string): Promise<void> {
  await (await getAxiosInstance()).delete(`/pur-subscription/customers/payment-methods/${paymentMethodId}`, config)
}

export async function getSubscription (config: OrcConfig): Promise<{ data: UserPurSubscription }> {
  return (await getAxiosInstance()).get<UserPurSubscription>('/pur-subscription/subscriptions/pur', config)
}

export async function addSubscription (config: OrcConfig): Promise<{ data: UserPurSubscription }> {
  return (await getAxiosInstance()).post<UserPurSubscription>('/pur-subscription/subscriptions/pur', {}, config)
}

export async function confirmSubscription (config: OrcConfig): Promise<void> {
  await (await getAxiosInstance()).post('/pur-subscription/subscriptions/pur/confirm', {}, config)
}

export async function deleteSubscription (config: OrcConfig): Promise<{ data: UserPurSubscription }> {
  return (await getAxiosInstance()).delete<UserPurSubscription>('/pur-subscription/subscriptions/pur', config)
}

export async function getSubscriptionProduct (config: OrcConfig): Promise<{ data: PurSubscriptionProduct }> {
  return (await getAxiosInstance()).get<PurSubscriptionProduct>('/pur-subscription/products/pur-subscription-product', config)
}

export async function getBillingAddress (config: OrcConfig): Promise<{ data: CustomerBillingAddress }> {
  return (await getAxiosInstance()).get<CustomerBillingAddress>('/pur-subscription/customers/billing-address', config)
}

export async function addBillingAddress (config: OrcConfig, payload: CustomerBillingAddress): Promise<void> {
  await (await getAxiosInstance()).put('/pur-subscription/customers/billing-address', payload, config)
}

export async function addCard (config: OrcConfig, cardToken: string): Promise<void> {
  await (await getAxiosInstance()).post('/pur-subscription/customers/payment-methods/card', { cardToken }, config)
}

export async function addDefaultPaymentMethod (config: OrcConfig, paymentMethodId: string): Promise<void> {
  await (await getAxiosInstance()).put('/pur-subscription/customers/payment-methods/default', { paymentMethodId }, config)
}

export async function revokeSubscription (config: OrcConfig) {
  return (await getAxiosInstance()).post('/pur-subscription/subscriptions/pur/revoke', {}, config)
}
